import { useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Send, ImageIcon, Plus, Trash2, BookOpen, StickyNote, Tag } from 'lucide-react';
import { useProject } from '../../hooks/useProjects';
import { useMedidas, useBatchUpsertMedidas, useSubmitMedidas } from '../../hooks/useMedidas';
import { useMedidaSets } from '../../hooks/useMedidaSets';
import { useMedidaFotos, useCreateMedidaFoto } from '../../hooks/useMedidaFotos';
import { useRecipes, type RecipeListDTO } from '../../hooks/useRecipes';
import { useAplicaciones, useCreateAplicacion } from '../../hooks/useAplicaciones';
import { useAuthStore } from '../../stores/auth-store';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { Modal } from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import { FileUpload, type UploadedFile } from '../../components/shared/FileUpload';
import { FotoThumbnail } from '../../components/shared/FotoThumbnail';
import { CreateRecipeModal } from '../../components/shared/CreateRecipeModal';
import { generateItemCode } from '../../lib/item-utils';

const UNIDAD_OPTIONS = [
  { value: 'm2', label: 'm²' },
  { value: 'ml', label: 'ml' },
  { value: 'unid', label: 'unid' },
  { value: 'kg', label: 'kg' },
  { value: 'glb', label: 'glb' },
];

// ── Item type ──

interface MedidaItem {
  tempId: string;
  recipeId: string | null;
  aplicacionId: string | null;
  codigo: string;
  nombre: string;
  categoria: string;
  unidad: string;
  cantidad: number;
  isManual: boolean;
}

let nextTempId = 1;
function genTempId() {
  return `temp-${nextTempId++}`;
}

export function MedidasPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const setId = searchParams.get('setId') ?? null;
  const user = useAuthStore((s) => s.user);

  const { data: project, isLoading: projectLoading } = useProject(projectId ?? '');
  // Load only medidas for this set (or orphans if no set)
  const { data: medidas, isLoading: medidasLoading } = useMedidas(
    projectId,
    undefined,
    setId ? undefined : true,   // without_set=true only when no setId
    setId ?? undefined,          // set_id filter when setId present
  );
  const { data: medidaSetsData } = useMedidaSets(projectId);
  const currentSet = setId ? medidaSetsData?.find((s) => s.id === setId) : null;
  const batchUpsert = useBatchUpsertMedidas();
  const submitMedidas = useSubmitMedidas();

  const { data: fotos } = useMedidaFotos(projectId);
  const createFoto = useCreateMedidaFoto();
  const { data: aplicacionesData } = useAplicaciones();

  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showCreateRecipe, setShowCreateRecipe] = useState(false);
  const [showCreateCategoria, setShowCreateCategoria] = useState(false);
  const [nuevaCategoriaNombre, setNuevaCategoriaNombre] = useState('');
  const createAplicacion = useCreateAplicacion();
  const [notas, setNotas] = useState('');
  const [notasInitialized, setNotasInitialized] = useState(false);

  // Initialize notas: from set's descripcion for named sets, from project otherwise
  if (!notasInitialized) {
    if (setId) {
      if (medidaSetsData !== undefined) {
        const set = medidaSetsData.find((s) => s.id === setId);
        setNotas(set?.descripcion ?? '');
        setNotasInitialized(true);
      }
    } else if (project) {
      setNotas(project.medidas_notas ?? '');
      setNotasInitialized(true);
    }
  }

  // Local items state
  const [items, setItems] = useState<MedidaItem[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Initialize items from existing medidas (once)
  if (!initialized && medidas && !medidasLoading) {
    const initial: MedidaItem[] = [];
    for (const m of medidas) {
      initial.push({
        tempId: genTempId(),
        recipeId: m.recipe_id,
        aplicacionId: m.aplicacion_id,
        codigo: m.recipe_codigo ?? '',
        nombre: m.item_tipo,
        categoria: m.rubro_tipo,
        unidad: m.unidad,
        cantidad: m.cantidad,
        isManual: !m.recipe_id,
      });
    }
    setItems(initial);
    setInitialized(true);
  }

  const isEditable = useMemo(() => {
    if (!project) return false;
    return !['proforma_aprobada', 'anticipo_registrado', 'pre_liquidacion', 'finalizado'].includes(project.estado);
  }, [project]);

  const canEdit = isEditable && user?.role !== 'compras';

  // ── Handlers ──

  const handleAddRow = useCallback(() => {
    setItems((prev) => [...prev, {
      tempId: genTempId(),
      recipeId: null,
      aplicacionId: null,
      codigo: '',
      nombre: '',
      categoria: 'gypsum',
      unidad: 'm2',
      cantidad: 0,
      isManual: true,
    }]);
  }, []);

  const handleAddRecipe = useCallback((recipe: RecipeListDTO) => {
    setItems((prev) => [...prev, {
      tempId: genTempId(),
      recipeId: recipe.id,
      aplicacionId: null,
      codigo: recipe.codigo,
      nombre: recipe.nombre,
      categoria: recipe.categoria,
      unidad: recipe.unidad,
      cantidad: 0,
      isManual: false,
    }]);
    setShowRecipeModal(false);
  }, []);

  const handleRemoveItem = useCallback((tempId: string) => {
    setItems((prev) => prev.filter((i) => i.tempId !== tempId));
  }, []);

  const handleFieldChange = useCallback((tempId: string, field: keyof MedidaItem, value: string | number | null) => {
    setItems((prev) => prev.map((i) => {
      if (i.tempId !== tempId) return i;
      const updated = { ...i, [field]: value };
      // Auto-generate code for manual items when category or description changes
      if (updated.isManual && (field === 'categoria' || field === 'nombre')) {
        updated.codigo = generateItemCode(updated.categoria, updated.nombre);
      }
      return updated;
    }));
  }, []);

  const handleSave = async () => {
    if (!projectId) return;

    const batchItems = items.map((i) => ({
      recipeId: i.recipeId,
      aplicacionId: i.aplicacionId,
      codigo: i.codigo,
      rubroTipo: i.categoria as 'gypsum' | 'pintura' | 'impermeabilizacion' | 'empaste',
      itemTipo: i.nombre,
      cantidad: i.cantidad,
      unidad: i.unidad as 'm2' | 'ml' | 'unid' | 'kg' | 'glb',
    }));

    try {
      await batchUpsert.mutateAsync({ projectId, medidaSetId: setId, items: batchItems, notas: notas || null });
    } catch {
      toast('error', 'Error al guardar medidas');
    }
  };

  // Photo upload — track registered keys to avoid duplicates
  // (onFilesChange passes the FULL array each time, not just new files)
  const registeredFotoKeys = useRef(new Set<string>());

  const handleFilesChange = async (files: UploadedFile[]) => {
    if (!projectId) return;
    for (const file of files) {
      if (file.s3_key && !registeredFotoKeys.current.has(file.s3_key)) {
        registeredFotoKeys.current.add(file.s3_key);
        try {
          await createFoto.mutateAsync({
            projectId,
            data: { s3Key: file.s3_key, filename: file.filename, mimeType: file.mime_type as 'image/jpeg' | 'image/png' | 'image/heic' | 'application/pdf' },
          });
        } catch { /* hook handles toast */ }
      }
    }
  };

  // ── Loading ──

  if (projectLoading || medidasLoading) {
    return (
      <div className="space-y-6 fade-up">
        <Skeleton className="h-8 w-64" />
        <Card><Skeleton className="h-96 w-full" /></Card>
      </div>
    );
  }

  if (!project) {
    return <p className="text-gray-500">Proyecto no encontrado</p>;
  }

  return (
    <div className="space-y-6 fade-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/proyectos/${projectId}`} className="text-gray-400 hover:text-brand-dark">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-brand-dark">
            {currentSet ? currentSet.nombre : 'Registrar Medidas'}
          </h1>
          <p className="text-sm text-gray-500">{project.nombre} — {project.codigo}</p>
        </div>
      </div>

      {/* Items table (ProformaBuilder style — quantity only) */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="w-8 px-2 py-3">#</th>
                <th className="px-2 py-3">Código</th>
                <th className="min-w-[200px] px-2 py-3">Descripción</th>
                <th className="px-2 py-3">Categoría</th>
                <th className="px-2 py-3">Und.</th>
                <th className="px-2 py-3 text-right">Cant.</th>
                {canEdit && <th className="w-10 px-2 py-3" />}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.tempId} className="border-b border-gray-50 hover:bg-gray-50/30">
                  <td className="px-2 py-2 text-xs text-gray-400">{index + 1}</td>
                  <td className="px-2 py-2">
                    <span className="font-mono text-xs text-gray-400">{item.codigo || '—'}</span>
                  </td>
                  <td className="px-2 py-2">
                    {canEdit && item.isManual ? (
                      <input
                        value={item.nombre}
                        onChange={(e) => handleFieldChange(item.tempId, 'nombre', e.target.value)}
                        placeholder="Descripción del ítem"
                        className="w-full min-w-[180px] rounded border border-gray-200 bg-white px-2 py-1 text-xs focus:border-brand-blue focus:outline-none"
                      />
                    ) : (
                      <span className="text-xs text-gray-700">{item.nombre}</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {canEdit ? (
                      <select
                        value={item.aplicacionId ?? ''}
                        onChange={(e) => handleFieldChange(item.tempId, 'aplicacionId', e.target.value || null)}
                        className="rounded border border-gray-200 bg-white px-1 py-1 text-xs focus:border-brand-blue focus:outline-none"
                      >
                        <option value="">—</option>
                        {(aplicacionesData ?? []).filter((a) => a.is_active).map((a) => (
                          <option key={a.id} value={a.id}>{a.nombre}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-gray-500">
                        {(aplicacionesData ?? []).find((a) => a.id === item.aplicacionId)?.nombre ?? '—'}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {canEdit && item.isManual ? (
                      <select
                        value={item.unidad}
                        onChange={(e) => handleFieldChange(item.tempId, 'unidad', e.target.value)}
                        className="rounded border border-gray-200 bg-white px-1 py-1 text-xs focus:border-brand-blue focus:outline-none"
                      >
                        {UNIDAD_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-gray-500">{item.unidad}</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {canEdit ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.cantidad || ''}
                        placeholder="0"
                        onChange={(e) => {
                          const num = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          if (!isNaN(num) && num >= 0) handleFieldChange(item.tempId, 'cantidad', num);
                        }}
                        className="w-24 rounded border border-gray-200 bg-white px-2 py-1 text-right text-xs focus:border-brand-blue focus:outline-none"
                      />
                    ) : (
                      <span className="text-xs">{item.cantidad}</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-2 py-2">
                      <button
                        onClick={() => handleRemoveItem(item.tempId)}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add row buttons — same as ProformaBuilder */}
        {canEdit && (
          <div className="flex gap-2 border-t border-gray-100 px-4 py-3">
            <Button variant="ghost" size="sm" leftIcon={<BookOpen size={14} />} onClick={() => setShowRecipeModal(true)}>
              Desde APU
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => setShowCreateRecipe(true)}
            >
              Crear nuevo APU
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Tag size={14} />}
              onClick={() => { setNuevaCategoriaNombre(''); setShowCreateCategoria(true); }}
            >
              Crear categoría
            </Button>
          </div>
        )}

        {items.length === 0 && (
          <div className="px-6 py-8 text-center">
            <p className="text-sm text-gray-400">No hay medidas registradas para este proyecto.</p>
          </div>
        )}
      </Card>

      {/* Summary */}
      {items.length > 0 && (
        <Card>
          <div className="p-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-brand-dark">{items.filter((i) => i.cantidad > 0).length}</span> ítem(s) con medidas
            </p>
          </div>
        </Card>
      )}

      {/* Notes */}
      <Card>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <StickyNote size={16} className="text-gray-400" />
            Notas
          </h3>
          {canEdit ? (
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Observaciones, detalles adicionales, instrucciones..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30 min-h-[100px] resize-y"
              maxLength={5000}
            />
          ) : (
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {notas || <span className="text-gray-400 italic">Sin notas</span>}
            </p>
          )}
        </div>
      </Card>

      {/* Photos */}
      <Card>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <ImageIcon size={16} className="text-gray-400" />
            Fotos de Medidas
          </h3>
          {canEdit && (
            <FileUpload
              context="medida_foto"
              onFilesChange={handleFilesChange}
              maxFiles={20}
            />
          )}
          {fotos && fotos.length > 0 && (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {fotos.map((foto) => (
                <FotoThumbnail key={foto.id} s3Key={foto.s3_key} filename={foto.filename} mimeType={foto.mime_type} />
              ))}
            </div>
          )}
          {(!fotos || fotos.length === 0) && !canEdit && (
            <p className="text-sm text-gray-400 py-4 text-center">No hay fotos registradas</p>
          )}
        </div>
      </Card>

      {/* Sticky save bar */}
      {canEdit && items.length > 0 && (
        <div className="sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-gray-200 p-4 -mx-4 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            Guarda primero, luego <strong>Enviar</strong> para notificar al administrativo
          </p>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={handleSave}
              isLoading={batchUpsert.isPending}
              leftIcon={<Save size={16} />}
            >
              Guardar
            </Button>
            <Button
              variant="primary"
              onClick={() => projectId && submitMedidas.mutate(projectId)}
              isLoading={submitMedidas.isPending}
              leftIcon={<Send size={16} />}
            >
              Enviar al administrativo
            </Button>
          </div>
        </div>
      )}

      {/* ── Recipe Picker Modal (same as ProformaBuilder) ── */}
      <RecipePickerModal
        isOpen={showRecipeModal}
        onClose={() => setShowRecipeModal(false)}
        onSelect={handleAddRecipe}
      />

      {/* Create Recipe Modal (inline, no navigation) */}
      <CreateRecipeModal
        isOpen={showCreateRecipe}
        onClose={() => setShowCreateRecipe(false)}
        onCreated={(recipe) => {
          setItems((prev) => [...prev, {
            tempId: genTempId(),
            recipeId: recipe.id,
            aplicacionId: null,
            codigo: recipe.codigo,
            nombre: recipe.nombre,
            categoria: recipe.categoria,
            unidad: recipe.unidad,
            cantidad: 0,
            isManual: false,
          }]);
        }}
      />

      {/* Create Categoria Modal */}
      <Modal isOpen={showCreateCategoria} title="Crear categoría" onClose={() => setShowCreateCategoria(false)} size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
              placeholder="Ej: Detalles Lineales, Forramiento, Paredes, Tumbado"
              value={nuevaCategoriaNombre}
              onChange={(e) => setNuevaCategoriaNombre(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowCreateCategoria(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={!nuevaCategoriaNombre.trim() || createAplicacion.isPending}
              onClick={async () => {
                if (!nuevaCategoriaNombre.trim()) return;
                await createAplicacion.mutateAsync({ nombre: nuevaCategoriaNombre.trim() });
                setShowCreateCategoria(false);
              }}
            >
              {createAplicacion.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Recipe Picker Modal (same style as ProformaBuilder) ──
function RecipePickerModal({
  isOpen,
  onClose,
  onSelect,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (recipe: RecipeListDTO) => void;
}) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useRecipes({ search: search || undefined, limit: 50 });

  return (
    <Modal isOpen={isOpen} title="Seleccionar APU" onClose={onClose} size="lg">
      <div className="space-y-4">
        <div className="relative">
          <input
            type="text"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            placeholder="Buscar APU por código o nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-gray-400">Cargando APUs...</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {data?.data.map((recipe) => (
              <button
                key={recipe.id}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-50"
                onClick={() => onSelect(recipe)}
              >
                <div>
                  <p className="font-medium text-gray-800">{recipe.codigo} — {recipe.nombre}</p>
                  <p className="text-xs text-gray-400">
                    MO: ${recipe.precio_mo_base.toFixed(2)} | Mat: ${recipe.precio_materiales_base.toFixed(2)} | Util: {recipe.porcentaje_utilidad_base}%
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xs text-gray-400">{recipe.categoria}</span>
                </div>
              </button>
            ))}
            {data?.data.length === 0 && (
              <p className="py-4 text-center text-sm text-gray-400">No se encontraron APUs</p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
